import { useEffect, useCallback } from "react";
import { create } from "zustand";
import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { FormattedMessage } from "react-intl";
import { get } from "lodash-es";
import { useForm } from "react-hook-form";
import { useCustomerStore } from "./CustomerProvider";

export const useBackInStock = create((set, get) => ({
  isOpen: false,
  product: null,
  setOpen: (product) => set({ product, isOpen: true }),
  setClose: () => set({ product: null, isOpen: false }),
}));

export default function BackInStock() {
  const { isOpen, product, setClose } = useBackInStock();
  const { customer } = useCustomerStore();
  const [selectedVariant, setSelectedVariant] = useState(null);

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (customer?.email) {
      setValue("email", customer.email);
    }
  }, [customer]);

  useEffect(() => {
    if (isOpen && product.variants.nodes.length > 0) {
      setSelectedVariant(
        product.variants.nodes[0].id.replace(
          "gid://shopify/ProductVariant/",
          ""
        )
      );
    }
  }, [isOpen, product]);

  const onSubmit = useCallback(
    async (data) => {
      if (!window?.BIS || !data?.email) return;

      const email = data.email;
      const productId = product?.id.replace("gid://shopify/Product/", "");
      const response = await window?.BIS.create(
        email,
        selectedVariant,
        productId
      );

      if (response.status === "Error") {
        window?.Alpine.store("toast").create(response?.errors?.base?.[0]);
        return false;
      }

      if (response.data) {
        if (get(response, "data.0.message")) {
          window?.Alpine.store("toast").create(get(response, "data.0.message"));

          setClose();
        }
      }
    },
    [selectedVariant, product]
  );

  return (
    <Dialog open={isOpen} onClose={() => setClose()}>
      <Dialog.Panel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <div
              aria-hidden="true"
              className="z-40 flex overflow-y-auto overflow-x-hidden fixed justify-center items-center h-full inset-0"
            >
              <div className="fixed inset-0 w-full h-full flex items-center">
                <div
                  className="z-50 w-full lg:max-w-lg lg:mx-auto absolute left-0 right-0 bottom-0 lg:relative bg-white lg:rounded-lg shadow  flex flex-col"
                  style={{
                    maxHeight: "100%",
                    boxShadow: "0 0 15px 0 #33333338",
                  }}
                >
                  <div className="flex justify-between items-center px-4 lg:px-8 py-3 lg:py-4 rounded-t border-b  ">
                    <h3 className="line-clamp-1 text-base lg:text-lg font-medium text-gray-900">
                      <FormattedMessage
                        id="product.notify_me"
                        defaultMessage="Notify Me"
                      />
                    </h3>
                    <button
                      type="button"
                      className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center "
                      onClick={() => setClose()}
                    >
                      <svg
                        className="pointer-events-none w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          clipRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          fillRule="evenodd"
                        ></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex flex-1 overflow-x-hidden px-4 pt-4 lg:px-8 lg:pt-8">
                    <div className="w-full">
                      <p className="text-gray-500 mb-3">
                        <FormattedMessage id="backinstock.description" />
                      </p>
                      {product && (
                        <div className="flex flex-col">
                          {product.variants.nodes.length == 1 ? (
                            <div className="font-medium text-lg mb-2">
                              {product.title}
                            </div>
                          ) : (
                            <select
                              value={selectedVariant}
                              onChange={(e) =>
                                setSelectedVariant(e.target.value)
                              }
                              className="form-select w-full border border-gray-200 mb-2"
                            >
                              {product.variants.nodes.map((variant) => {
                                if (variant.availableForSale) {
                                  return null;
                                }

                                const id = variant.id.replace(
                                  "gid://shopify/ProductVariant/",
                                  ""
                                );

                                return (
                                  <option
                                    key={variant.id}
                                    value={id}
                                    className="flex flex-row"
                                  >
                                    {product.variants.nodes.length === 1
                                      ? product.title
                                      : variant.title}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                        </div>
                      )}
                      <div>
                        <input
                          {...register("email", {
                            required: "Email is required",
                          })}
                          className="form-input py-2 px-4 w-full border border-gray-200 rounded text-black/90"
                          placeholder="Email Address"
                        />
                        {errors?.email && (
                          <p className="text-red-500">
                            {errors?.email?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 lg:px-8 lg:py-8">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        className="w-full bg-white text-black/90 border border-gray-300 text-base font-medium py-3 px-3 text-center"
                        onClick={() => setClose()}
                      >
                        <FormattedMessage
                          id="modal.cancel"
                          defaultMessage="Cancel"
                        />
                      </button>
                      <button
                        className="w-full bg-primary text-white border border-primary text-base font-medium py-3 px-3 text-center"
                        type="submit"
                        onClick={handleSubmit}
                      >
                        <FormattedMessage
                          id="modal.notify_me"
                          defaultMessage="Notify Me"
                        />
                      </button>
                    </div>
                  </div>
                  {window?.MobileDetect.isApp && window?.MobileDetect.isIOS && (
                    <div
                      style={{ height: "120px" }}
                      class="block lg:hidden"
                    ></div>
                  )}
                </div>
                <div
                  className="fixed inset-0 w-full h-full z-30 bg-gray-900 opacity-70"
                  onClick={() => setClose()}
                ></div>
              </div>
            </div>
          </div>
        </form>
      </Dialog.Panel>
    </Dialog>
  );
}
